import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCallback, useEffect, useState } from "react";
import { useAlert } from "../../../../../Contexts/AlertContext/AlertService";
import { useConfirm } from "../../../../../Contexts/ConfirmContext/ConfirmService";
import { useLoading } from "../../../../../providers/loadingProvider";
import { useNotify } from "../../../../../providers/NotificationProvider";
import { finalRecommendations } from "../../../Constants";
import service from "../../../Helpers/gifservices";
import CancelIcon from "@mui/icons-material/Cancel";

const gradingTip = `Please select your final grade`;
const rankingTip = `Please rank this proposal in comparison to the rest of the proposal in your committee ( i.e. 1/20 which means first among 20 etc.)`;

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    width: "40%",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #000",
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: `15%`,
    left: `30%`,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
  },
  miniTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 5,
  },
  infoIcon: {
    marginLeft: 5,
  },
  select: {
    width: "70%",
  },
  saveButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  button: {
    marginLeft: 10,
  },
  closeIcon: {
    top: -20,
    left: -30,
    marginBottom: -50,
  },
}));

export default function PanelGradeModal(props) {
  const classes = useStyles();
  const loading = useLoading();
  const alert = useAlert();
  const notify = useNotify();
  const confirm = useConfirm();
  const {
    open,
    onClose,
    totalProposalsNumber,
    proposal,
    refreshProposals,
    readOnly,
  } = props;
  const [justification, setJustification] = useState("");
  const [recommend, setRecommend] = useState(``);
  const [rank, setRank] = useState(``);
  const [rankingOptions, setRankingOptions] = useState([]);

  const submitEvaluation = async () => {
    const res = await confirm({
      title: "Confirm Submit Evaluation",
      description:
        "Are you sure you want to submit the evaluation? Note!! changes cant be made after submiting!",
    });
    if (!res) return;
    const loadingId = loading.addLoading("Submiting Evaluation");
    const proposalId = proposal.id;
    var result = await service.UpdatePanelRanking({
      proposalId,
      rank,
      grade: recommend,
      justification,
      isfinal: true,
    });
    loading.removeLoading(loadingId);
    if (!result) {
      await alert({
        title: "Error Submiting Evaluation",
        description: `Sorry there was a problem in submiting the evaluation`,
      });
    } else {
      onClose();
      notify.show("Submiting evaluation completed successfully");
      refreshProposals();
    }
  };

  const draftEvaluation = async () => {
    const proposalId = proposal.id;
    onClose();
    var result = await service.UpdatePanelRanking({
      proposalId,
      rank,
      grade: recommend,
      justification,
      isfinal: false,
    });
    if (!result) {
      await alert({
        title: "Error draft Evaluation",
        description: `Sorry there was a problem in draft the evaluation`,
      });
    } else {
      notify.show("Draft evaluation completed successfully");
      refreshProposals();
    }
  };

  const buildRanking = useCallback(() => {
    let rankingArray = [];
    for (let i = 1; i <= totalProposalsNumber; i++)
      rankingArray.push(`${i}/${totalProposalsNumber}`);

    setRankingOptions(rankingArray);
  }, [totalProposalsNumber]);

  useEffect(() => {
    buildRanking();
  }, [buildRanking]);

  useEffect(() => {
    if (!proposal) return;
    if (!recommend && proposal.proposalFinalGrade)
      setRecommend(proposal.proposalFinalGrade);
    if (!rank && proposal.proposalFinalRank)
      setRank(proposal.proposalFinalRank);
    if (!justification && proposal.gradeandRankJustification)
      setJustification(proposal.gradeandRankJustification);
  }, [justification, rank, recommend, proposal]);

  return (
    <Modal open={open} onClose={onClose}>
      <Paper className={classes.paper}>
        <Grid container>
          <IconButton className={classes.closeIcon} onClick={onClose} size="large">
            <CancelIcon style={{ fontSize: 30 }} />
          </IconButton>
          <Box width="100%" marginBottom={3} textAlign="center">
            <Typography className={classes.title}>
              Panel Final Evaluation
            </Typography>
          </Box>
          <Grid item xs={6}>
            <Box display="flex" marginTop={2}>
              <Typography className={classes.miniTitle}>
                Proposal Final Ranking
              </Typography>
              <Tooltip title={rankingTip}>
                <InfoOutlinedIcon
                  className={classes.infoIcon}
                  color="primary"
                />
              </Tooltip>
            </Box>
            <Select
              disabled={readOnly}
              className={classes.select}
              value={rank}
              onChange={(e) => {
                setRank(e.target.value);
              }}
            >
              {rankingOptions.map((rank, index) => (
                <MenuItem key={rank} value={index + 1}>
                  {rank}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" marginTop={2}>
              <Typography className={classes.miniTitle}>
                Proposal Final Grading
              </Typography>
              <Tooltip title={gradingTip}>
                <InfoOutlinedIcon
                  className={classes.infoIcon}
                  color="primary"
                />
              </Tooltip>
            </Box>
            <Select
              disabled={readOnly}
              className={classes.select}
              value={recommend}
              onChange={(e) => setRecommend(e.target.value)}
            >
              {finalRecommendations.map((recommend) => (
                <MenuItem key={recommend.value} value={recommend.value}>
                  {recommend.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" marginTop={5}>
              <Typography className={classes.miniTitle}>
                Grading and Ranking Justification
              </Typography>
            </Box>
            <TextField
              disabled={readOnly}
              label="Justification"
              fullWidth
              multiline
              maxRows={10}
              rows={6}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
            ></TextField>
          </Grid>
        </Grid>
        {!readOnly && (
          <Box style={{ float: "right" }} marginTop={3}>
            <Button
              disableElevation
              variant="contained"
              className={classes.saveButton}
              onClick={submitEvaluation}
            >
              Submit Evaluation
            </Button>
            <Button
              disableElevation
              variant="contained"
              className={classes.button}
              onClick={draftEvaluation}
            >
              Save Draft
            </Button>
          </Box>
        )}
      </Paper>
    </Modal>
  );
}
